import { useEffect, useRef } from "react";
import type { LastBotCore } from "../core/LastBotCore";
import type { LastBotState } from "../core/state";
import type { Page, PageOptions } from "../types/page";
import { useCoreState } from "./useCoreState";

const threadIdSelector = (state: LastBotState): number | null =>
  state.thread?.id ?? null;

/**
 * Auto-navigates the default multi-page UI to the conversation page when a
 * NEW thread becomes active while another page is showing — e.g. an embedder
 * calls core.open() + core.sendMessage() in the same tick, or
 * core.startNewThread() from a "Chat about this" button on the host page
 * (LAS-1491).
 *
 * Derived from core state via useCoreState (useSyncExternalStore) instead of
 * the thread:opened event: an event that fires in the gap between
 * ChatWidget's mount and its effect subscriptions is lost forever, while a
 * state change landing in that gap is re-checked when the store subscription
 * attaches, so it cannot be missed. thread:opened itself stays public API
 * (docs/api.md § events) — only the default UI's internal consumer changed.
 *
 * Navigation fires only on an OBSERVED TRANSITION to a different non-null
 * thread id after mount:
 * - Mount with a pre-existing thread → no navigation. The mount-time page
 *   read already lands on the stored conversation page when appropriate
 *   (useThreadPagePersistence mirrors the thread into page storage,
 *   LAS-1492), and a user who deliberately parked on another page (chat
 *   history, landing) must not be yanked back on reopen.
 * - Page change while the same thread stays active (history, login) → no
 *   navigation, for the same reason.
 * - id → different id (startNewThread closes + reopens; React may batch the
 *   null hop away) → navigate, matching the old event-driven behavior.
 *
 * Concurrent-mode note: a thread resolving mid-render can't leave the
 * sentinel wedged on the new id with a stale landing page. ChatWidget mounts
 * from discrete/default-lane updates, which React renders synchronously
 * (only transitions time-slice), so a fetch can't interleave into the render
 * that reads both page storage and this selector; and if a yielded mount
 * were ever discarded and restarted, useState initializers re-run and
 * re-read storage — which useThreadPagePersistence updated in the same
 * synchronous emit as state.thread — landing directly on the conversation
 * page with no navigation needed.
 */
export const useThreadAutoNavigate = (
  core: LastBotCore,
  pageType: Page["type"],
  navigate: (_options: PageOptions) => void,
  hasCustomMainPage: boolean,
): void => {
  const threadId = useCoreState(core, threadIdSelector);

  // Latest-ref so the effect doesn't depend on ChatWidget re-creating its
  // navigate closure every render (same pattern as useCoreState's selectorRef).
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // undefined = "effect has not run yet", distinct from null = "no thread".
  const prevThreadIdRef = useRef<number | null | undefined>(undefined);

  useEffect(() => {
    const prev = prevThreadIdRef.current;
    prevThreadIdRef.current = threadId;
    if (prev === undefined) return; // first run after mount: record only
    if (threadId === null || threadId === prev) return;
    if (hasCustomMainPage) return; // CustomMainPage renders thread state in place
    if (pageType === "conversation") return;
    navigateRef.current({
      page: {
        type: "conversation",
        params: { messageThreadId: threadId.toString() },
      },
    });
  }, [threadId, pageType, hasCustomMainPage]);
};
